// Tracks which of the 16 grid cells currently have >=1 pointer/key down.
// Emits "press" when a cell transitions 0 -> 1, "release" when it goes 1+ -> 0.
//
// Slide behavior: a pointer that moves across the grid leaves its previous cell
// and enters the next. The "pressed" state is therefore the union of all such
// pointers per cell, plus keyboard keys.

export type CellListener = (cell: number, ev: { type: 'press' | 'release'; source: 'pointer' | 'keyboard'; time: number }) => void;

const KEY_MAP: Record<string, number> = {
  // Row 0
  '1': 0, '2': 1, '3': 2, '4': 3,
  // Row 1
  'q': 4, 'w': 5, 'e': 6, 'r': 7,
  // Row 2
  'a': 8, 's': 9, 'd': 10, 'f': 11,
  // Row 3
  'z': 12, 'x': 13, 'c': 14, 'v': 15,
};

export class InputManager {
  private cellSources: Array<Set<string>> = Array.from({ length: 16 }, () => new Set());
  private pointerCell = new Map<number, number>(); // pointerId -> last cell index
  private listeners = new Set<CellListener>();
  private hitTest!: (clientX: number, clientY: number) => number;

  attach(target: HTMLElement, hitTest: (clientX: number, clientY: number) => number) {
    this.hitTest = hitTest;
    target.style.touchAction = 'none';
    target.addEventListener('pointerdown', this.onPointerDown, { passive: false });
    target.addEventListener('pointermove', this.onPointerMove, { passive: false });
    target.addEventListener('pointerup', this.onPointerUp);
    target.addEventListener('pointercancel', this.onPointerUp);
    target.addEventListener('pointerleave', this.onPointerUp);
    target.addEventListener('lostpointercapture', this.onPointerUp);
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.onBlur);
  }

  detach(target: HTMLElement) {
    target.removeEventListener('pointerdown', this.onPointerDown);
    target.removeEventListener('pointermove', this.onPointerMove);
    target.removeEventListener('pointerup', this.onPointerUp);
    target.removeEventListener('pointercancel', this.onPointerUp);
    target.removeEventListener('pointerleave', this.onPointerUp);
    target.removeEventListener('lostpointercapture', this.onPointerUp);
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.onBlur);
  }

  onCell(fn: CellListener) { this.listeners.add(fn); return () => this.listeners.delete(fn); }

  isPressed(cell: number) { return this.cellSources[cell].size > 0; }

  private emit(cell: number, type: 'press' | 'release', source: 'pointer' | 'keyboard', time: number) {
    for (const l of this.listeners) l(cell, { type, source, time });
  }

  /** Add a source (pointer-N or key-X) to a cell. Returns true if cell transitioned to pressed. */
  private addSource(cell: number, source: string, kind: 'pointer' | 'keyboard', time: number) {
    if (cell < 0 || cell > 15) return;
    const set = this.cellSources[cell];
    const wasPressed = set.size > 0;
    set.add(source);
    if (!wasPressed) this.emit(cell, 'press', kind, time);
  }

  private removeSource(cell: number, source: string, kind: 'pointer' | 'keyboard', time: number) {
    if (cell < 0 || cell > 15) return;
    const set = this.cellSources[cell];
    if (!set.delete(source)) return;
    if (set.size === 0) this.emit(cell, 'release', kind, time);
  }

  private onPointerDown = (e: PointerEvent) => {
    e.preventDefault();
    (e.target as Element).setPointerCapture?.(e.pointerId);
    const cell = this.hitTest(e.clientX, e.clientY);
    if (cell < 0) return;
    this.pointerCell.set(e.pointerId, cell);
    this.addSource(cell, `p:${e.pointerId}`, 'pointer', e.timeStamp);
  };

  private onPointerMove = (e: PointerEvent) => {
    if (!this.pointerCell.has(e.pointerId)) return;
    const prev = this.pointerCell.get(e.pointerId)!;
    const next = this.hitTest(e.clientX, e.clientY);
    if (next === prev) return;
    if (prev >= 0) this.removeSource(prev, `p:${e.pointerId}`, 'pointer', e.timeStamp);
    if (next >= 0) this.addSource(next, `p:${e.pointerId}`, 'pointer', e.timeStamp);
    this.pointerCell.set(e.pointerId, next);
  };

  private onPointerUp = (e: PointerEvent) => {
    const prev = this.pointerCell.get(e.pointerId);
    if (prev === undefined) return;
    if (prev >= 0) this.removeSource(prev, `p:${e.pointerId}`, 'pointer', e.timeStamp);
    this.pointerCell.delete(e.pointerId);
  };

  private onKeyDown = (e: KeyboardEvent) => {
    if (e.repeat) return;
    const k = e.key.toLowerCase();
    const cell = KEY_MAP[k];
    if (cell === undefined) return;
    e.preventDefault();
    this.addSource(cell, `k:${k}`, 'keyboard', performance.now());
  };

  private onKeyUp = (e: KeyboardEvent) => {
    const k = e.key.toLowerCase();
    const cell = KEY_MAP[k];
    if (cell === undefined) return;
    this.removeSource(cell, `k:${k}`, 'keyboard', performance.now());
  };

  private onBlur = () => {
    // release all
    const t = performance.now();
    for (let i = 0; i < 16; i++) {
      if (this.cellSources[i].size > 0) {
        this.cellSources[i].clear();
        this.emit(i, 'release', 'keyboard', t);
      }
    }
    this.pointerCell.clear();
  };
}

export { KEY_MAP };
